import { Box, Flex, Text } from '@chakra-ui/react';
import { ErrorMessage, Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  getDataSourceApiPreviewEnabled,
  getNoheadersStatus,
  getSelectedDataFileDetails,
  getSelectedVersionDetails,
  getTableHeader,
  handleActionStepNext,
  resetMultipleTimePeriodValue,
  setSelectedAreas,
  setSingleTimePeriodValue,
  setTimeValueMap,
} from 'redux/DataImportSlice';
import { getModelTime } from 'redux/IndicatorPageSlice';

import colors from '../../../dataTheme';
import ActionStepFooter from '../../ActionStepFooter';
import {
  FieldError,
  FieldLabel,
  StepCheckbox,
  StepFooterRow,
  StepHeading,
  StepNotice,
  StepSelect,
} from '../../actionStepUi';

import { loadTimeValueOptions, type TimeColumnMapping } from './timeRowOptions';

const validationSchema = Yup.object().shape({
  singleTimePeriodValue: Yup.string().required('Time value is required'),
});

interface Properties {
  setHelpModalSeleceted?: any;
  getFormValue?: any;
}

type TimePeriodFormValues = {
  singleTimePeriodValue: string;
  periodMappings: TimeColumnMapping[];
};

const applySavedMappings = (
  mappings: TimeColumnMapping[],
  savedMappings: Array<{ key: string; value: string | null }> | null | undefined,
  modelPeriods: string[],
) => {
  if (!Array.isArray(savedMappings)) return mappings;

  const validModelPeriods = new Set(modelPeriods.map(String));
  const savedByValue = new Map(
    savedMappings.map((mapping) => [mapping.key, mapping.value ?? null]),
  );

  return mappings.map((mapping) => {
    if (!savedByValue.has(mapping.key)) return mapping;

    const savedValue = savedByValue.get(mapping.key);
    if (!savedValue || !validModelPeriods.has(String(savedValue))) return mapping;

    return {
      ...mapping,
      value: savedValue,
      autoMapped: true,
    };
  });
};

const getMappedPeriodCount = (mappings: TimeColumnMapping[]) =>
  mappings.filter((mapping) => Boolean(mapping.value)).length;

const Rows = ({ getFormValue }: Properties) => {
  const dispatch: any = useDispatch();
  const headerNames = useSelector(getTableHeader);
  const selectedVersion = useSelector(getSelectedVersionDetails);
  const selectedFile = useSelector(getSelectedDataFileDetails);
  const useApiPreview = useSelector(getDataSourceApiPreviewEnabled);
  const modelTime = useSelector(getModelTime);
  const isHeader = useSelector(getNoheadersStatus);
  const [customHeaders, setCustomHeaders] = useState<any[]>(headerNames ?? []);
  const [selectedTimeColumn, setSelectedTimeColumn] = useState(
    getFormValue?.singleTimePeriodValue || '',
  );
  const [timeValueOptions, setTimeValueOptions] = useState<TimeColumnMapping[]>([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [manualMapping, setManualMapping] = useState(false);
  const modelPeriods = useMemo(
    () => (Array.isArray(modelTime?.time_range) ? modelTime.time_range : []),
    [modelTime?.time_range],
  );

  useEffect(() => {
    if (isHeader) {
      const array: string[] = new Array(26)
        .fill(1)
        .map((_: string, index: number) => String.fromCodePoint(65 + index));
      const newHeaders = array
        .slice(0, headerNames.length)
        .map((item: string, headerIndex: number) => {
          return { current: `${headerNames[headerIndex]}`, modified: `Column ${item}` };
        });
      setCustomHeaders(newHeaders);
    } else {
      setCustomHeaders(
        headerNames.map((item: string) => {
          return { current: `${item}`, modified: `${item}` };
        }),
      );
    }
  }, [isHeader, headerNames]);

  useEffect(() => {
    setSelectedTimeColumn(getFormValue?.singleTimePeriodValue || '');
  }, [getFormValue?.singleTimePeriodValue]);

  useEffect(() => {
    let isCancelled = false;

    const loadTimeValues = async () => {
      if (
        !selectedVersion?.s3_file_url ||
        !headerNames?.length ||
        !selectedTimeColumn ||
        modelPeriods.length === 0
      ) {
        setTimeValueOptions([]);
        return;
      }

      setLoadingValues(true);
      setLoadError('');

      try {
        const options = await loadTimeValueOptions({
          s3FileUrl: selectedVersion.s3_file_url,
          headerNames,
          timeColumn: selectedTimeColumn,
          modelTimeRange: modelPeriods,
          dataSourceId: selectedFile?.id,
          versionId: selectedVersion?.id,
          useApiPreview,
        });
        if (isCancelled) return;
        setTimeValueOptions(options);
        setLoadingValues(false);
      } catch {
        if (isCancelled) return;
        setLoadError('Unable to read the selected file for time period detection.');
        setLoadingValues(false);
      }
    };

    loadTimeValues();

    return () => {
      isCancelled = true;
    };
  }, [
    headerNames,
    modelPeriods,
    selectedTimeColumn,
    selectedVersion?.s3_file_url,
    selectedVersion?.id,
    selectedFile?.id,
    useApiPreview,
  ]);

  const initialFormValues = useMemo<TimePeriodFormValues>(() => {
    return {
      singleTimePeriodValue: selectedTimeColumn,
      periodMappings: applySavedMappings(
        timeValueOptions,
        getFormValue?.timeValueMap,
        modelPeriods,
      ),
    };
  }, [getFormValue?.timeValueMap, modelPeriods, selectedTimeColumn, timeValueOptions]);

  const staleSavedMappingCount = useMemo(() => {
    if (!Array.isArray(getFormValue?.timeValueMap)) return 0;

    const validModelPeriods = new Set(modelPeriods.map(String));
    return getFormValue.timeValueMap.filter(
      (mapping: { value?: string | null }) =>
        mapping?.value && !validModelPeriods.has(String(mapping.value)),
    ).length;
  }, [getFormValue?.timeValueMap, modelPeriods]);

  const handleSubmit = async (
    form: TimePeriodFormValues,
    setSubmitting: (data: boolean) => void,
  ) => {
    const mappingsForPayload = (form.periodMappings || [])
      .filter((mapping) => mapping?.key && mapping?.value)
      .map((mapping) => ({
        key: mapping.key,
        value: mapping.value,
      }));

    await dispatch(setSingleTimePeriodValue(form));
    await dispatch(setTimeValueMap(mappingsForPayload.length > 0 ? mappingsForPayload : null));
    await dispatch(resetMultipleTimePeriodValue());
    await setSubmitting(false);
    await dispatch(handleActionStepNext());
  };

  return (
    <Box w='100%'>
      <StepNotice mb={4}>Select the column which has the time periods for each row.</StepNotice>
      <Formik
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => {
          const periodMappings: TimeColumnMapping[] = values?.periodMappings || [];
          const mappedPeriodCount = getMappedPeriodCount(periodMappings);
          const hasUnmappedPeriods = periodMappings.some((mapping) => !mapping.value);
          const mappingControlsEnabled = manualMapping || hasUnmappedPeriods;
          const updatePeriodMapping = async (mappingIndex: number, period: string) => {
            const nextMappings = periodMappings.map((mapping, index) =>
              index === mappingIndex
                ? {
                    ...mapping,
                    value: period || null,
                    autoMapped: false,
                  }
                : mapping,
            );
            await setFieldValue('periodMappings', nextMappings);
          };

          return (
            <Form style={{ width: '100%' }}>
              <FieldLabel>Time period column</FieldLabel>
              <StepSelect
                value={values?.singleTimePeriodValue}
                placeholder='Select column'
                isClearable
                isInvalid={Boolean(errors.singleTimePeriodValue)}
                options={customHeaders.map((header: any) => ({
                  value: header.current,
                  label: header.modified,
                }))}
                onChange={async (role) => {
                  await setFieldValue('singleTimePeriodValue', role);
                  await setFieldValue('periodMappings', []);
                  setSelectedTimeColumn(String(role));
                  setManualMapping(false);
                  await dispatch(setSelectedAreas({ selectedColumnOption: role }));
                }}
              />
              <ErrorMessage name='singleTimePeriodValue' component={FieldError} />

              {values?.singleTimePeriodValue && !loadingValues && periodMappings.length === 0 && (
                <StepNotice tone='danger' pt={2}>
                  No date-like time period values were detected in the selected column.
                </StepNotice>
              )}

              {loadError && (
                <StepNotice tone='danger' pt={2}>
                  {loadError}
                </StepNotice>
              )}

              {periodMappings.length > 0 && (
                <Flex direction='column' gap={3} pt={5} w='100%'>
                  <StepCheckbox
                    isChecked={mappingControlsEnabled}
                    isDisabled={hasUnmappedPeriods}
                    onChange={setManualMapping}
                  >
                    Map manually
                  </StepCheckbox>

                  {hasUnmappedPeriods && (
                    <StepNotice tone='warning'>
                      Some detected values do not match the model timeline. Select target periods
                      below; unmapped rows will be skipped.
                    </StepNotice>
                  )}

                  {staleSavedMappingCount > 0 && (
                    <StepNotice tone='warning'>
                      Some saved time period mappings are no longer in the current model timeline.
                      Please remap them before continuing.
                    </StepNotice>
                  )}

                  <StepHeading title='Map the values into Blox' />

                  <Flex alignItems='center' gap={3} mt='-8px'>
                    <FieldLabel flex={1} mb={0}>
                      Time period in file
                    </FieldLabel>
                    <Box w='16px' />
                    <FieldLabel flex={1} mb={0}>
                      Time period in Blox
                    </FieldLabel>
                  </Flex>

                  <Flex direction='column' gap={3} className='scroll' maxH='25vh' overflowY='auto'>
                    {periodMappings.map((mapping, index) => (
                      <Flex key={`${mapping.key}-${index}`} alignItems='center' gap={3} w='100%'>
                        <Text
                          flex={1}
                          minW={0}
                          {...PLAN_TEXT.bodyMedium}
                          color={colors.textDark}
                          noOfLines={1}
                          title={mapping.sourceValue || mapping.key}
                        >
                          {mapping.sourceValue || mapping.key}
                        </Text>
                        <FiArrowRight size={16} color={colors.textSecondary} />
                        <Box flex={1} minW={0}>
                          <StepSelect
                            value={mapping.value || ''}
                            placeholder='Select period'
                            isClearable
                            isDisabled={!mappingControlsEnabled}
                            options={modelPeriods.map((period: string) => ({
                              value: period,
                              label: period,
                            }))}
                            onChange={(period) => updatePeriodMapping(index, String(period))}
                          />
                        </Box>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              )}

              <StepFooterRow>
                <ActionStepFooter
                  nextButtonName='Continue'
                  prevButtonName='Previous'
                  isSubmitting={isSubmitting}
                  disableNext={
                    !values?.singleTimePeriodValue ||
                    loadingValues ||
                    periodMappings.length === 0 ||
                    mappedPeriodCount === 0
                  }
                />
              </StepFooterRow>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default Rows;
